<script setup lang="ts">
import { Button } from '@/components/ui/button';
import BlockingLoading from '@/ui-plus/BlockingLoading.vue';
import { formatDate } from '@lib/utils/formatDate';
import { onMounted, ref } from 'vue';
import { toast } from '@/ui-plus/sonner';
import type { ResType } from "@api-client/typeUtils";
import { authGlobalApiClient } from '@api-client/auth_global';
import { passkeyRegister } from '@auth-frontend-vue/lib/auth.actions';
import { clearPasskeyUsedLocally, setPasskeyUsedLocally } from '@auth-frontend-vue/lib/passkeyLocalStorage';
import { KeyRound, RefreshCw, Trash2 } from 'lucide-vue-next';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = withDefaults(defineProps<{
	allowMutations?: boolean;
}>(), {
	allowMutations: true,
});

const isSupported = browserSupportsWebAuthn();

type Passkey = ResType<typeof authGlobalApiClient.passkey.passkeylist.$post>["data"]["passkeys"][number];

const loading = ref(true);
const registering = ref(false);
const passkeys = ref<Passkey[]>([]);

async function loadPasskeys() {
	loading.value = true;
	const { data, error } = await (await authGlobalApiClient.passkey.passkeylist.$post()).json();
	loading.value = false;
	if (error) {
		toast.error(error.message ?? 'Failed to load passkeys');
		return;
	}
	passkeys.value = data?.passkeys ?? [];
	if (passkeys.value.length === 0) {
		clearPasskeyUsedLocally();
	} else {
		setPasskeyUsedLocally();
	}
}

function requireMutations(): boolean {
	if (props.allowMutations) return true;
	toast.error(t('freshSessionRequired'), {
		description: t('pleaseLogoutAndLoginAgain'),
	});
	return false;
}

async function addPasskey() {
	if (!requireMutations()) return;
	if (passkeys.value.length > 20) {
		toast.error('You can only have up to 20 passkeys, remove some to add a new one');
		return;
	}
	if (!isSupported) return;
	if (registering.value) return;
	registering.value = true;
	try {
		const { data, error } = await passkeyRegister();
		if (error) {
			if (error.code !== 'PASSKEY_CANCELLED') {
				toast.error(error.message ?? 'Failed to add passkey');
			}
			return;
		}
		toast.success(`Added passkey: ${data?.name ?? 'Passkey'}`);
		await loadPasskeys();
	} finally {
		registering.value = false;
	}
}

async function removePasskey(passkey: Passkey) {
	if (!requireMutations()) return;
	const { error } = await (await authGlobalApiClient.passkey['passkey-delete'].$post({
		json: {
			id: passkey.id,
		},
	})).json();
	if (error) {
		toast.error(error.message ?? 'Failed to remove passkey');
		return;
	}
	toast.success('Passkey removed');
	await loadPasskeys();
}

onMounted(() => {
	if (!isSupported) return;
	loadPasskeys();
});
</script>

<template>
	<div class="space-y-4"
		 v-if="isSupported">
		<BlockingLoading v-if="registering" />
		<div class="flex items-center justify-between gap-3">
			<h2 class="text-lg font-semibold">Passkeys</h2>
			<div class="flex items-center gap-2">
				<Button @click="loadPasskeys"
						:disabled="loading"
						variant="outline"
						size="sm">
					<RefreshCw :class="{ 'animate-spin': loading }"
							   class="h-4 w-4" />
				</Button>
				<Button @click="addPasskey"
						:disabled="registering || !allowMutations"
						size="sm">
					<KeyRound class="h-4 w-4 mr-1" />
					Add passkey
				</Button>
			</div>
		</div>

		<p v-if="loading"
		   class="text-sm text-muted-foreground">
			Loading...
		</p>

		<p v-else-if="passkeys.length === 0"
		   class="text-sm text-muted-foreground">
			No passkeys yet
		</p>

		<div v-else
			 class="space-y-3">
			<div v-for="passkey in passkeys"
				 :key="passkey.id"
				 class="flex flex-col gap-2 rounded-lg border border-border/50 bg-accent p-4 sm:flex-row sm:items-center sm:justify-between">
				<div class="min-w-0">
					<p class="font-medium">{{ passkey.name }}</p>
					<p class="text-sm text-muted-foreground">
						Added {{ formatDate(passkey.createdAt, undefined, true) }}
					</p>
					<p v-if="passkey.lastUsedAt"
					   class="text-sm text-muted-foreground">
						Last used {{ formatDate(passkey.lastUsedAt, undefined, true) }}
					</p>
				</div>
				<Button variant="outline"
						size="sm"
						:disabled="!allowMutations"
						@click="removePasskey(passkey)">
					<Trash2 class="h-4 w-4" />
					Remove
				</Button>
			</div>
		</div>
	</div>
</template>
